import { apiInstance } from '@/api/instance';

/** Contract: bodyprint-api/docs/external-apis/partners-contract.md (Campanhas e resultados). The partner only reads. */
export type PartnerCampaignStatus = 'approved' | 'live' | 'paused' | 'ended';

export type PlacementSlot =
  'marketplace_top' | 'body_metrics' | 'profile' | 'planning' | 'plus_club';

export interface PartnerCampaign {
  id: string;
  name: string;
  status: PartnerCampaignStatus;
  startsAt: string;
  endsAt: string | null;
  slots: PlacementSlot[];
  offer: { id: string; title: string };
  /** The audience as the Bodyprint describes it; never the rule nor its size. Null on the Clube. */
  audience: { description: string | null } | null;
  /**
   * `BODYPRINT_PLUS` on an Exclusivo Bodyprint+ campaign (Clube Bodyprint+): only members use the
   * coupon, and the code is the same for every member.
   */
  requiredConsumerProduct: 'BODYPRINT_PLUS' | null;
}

export interface PartnerCampaignsListParams {
  offset?: number;
  limit?: number;
}

export interface PartnerCampaignsListResponse {
  items: PartnerCampaign[];
  count: number;
}

export interface CampaignReportCounts {
  impressions: number;
  reach: number;
  clicks: number;
  dismisses: number;
  leads: number;
  /** Times the coupon was copied. */
  couponCopies: number;
  /** clicks ÷ impressions; null without impressions. */
  ctr: number | null;
}

/** A slice under 20 people keeps its key and hides every number. */
type CampaignReportSlice<Key extends string, Value extends string> = {
  [K in Key]: Value;
} & (
  | (CampaignReportCounts & { hidden: false })
  | ({ [K in keyof CampaignReportCounts]: null } & { hidden: true })
);

export interface CampaignReport {
  campaignId: string;
  range: { from: string; to: string };
  totals: CampaignReportCounts;
  byDay: CampaignReportSlice<'day', string>[];
  byVariant: CampaignReportSlice<'variantKey', string>[];
  bySlot: CampaignReportSlice<'slot', PlacementSlot>[];
  /** Clicks per button (the action's `kind`), most clicked first. */
  byAction: CampaignReportActionSlice[];
}

/** Hidden when fewer than 20 people clicked that button. */
type CampaignReportActionSlice = { action: string } & (
  { clicks: number; people: number; hidden: false } | { clicks: null; people: null; hidden: true }
);

export interface CampaignReportParams {
  from?: string;
  to?: string;
}

interface CampaignLead {
  id: string;
  name: string;
  email: string | null;
  phone: string | null;
  slot: PlacementSlot;
  variantKey: string | null;
  consentedAt: string;
  consentText: string;
}

export interface CampaignLeadsParams {
  offset?: number;
  limit?: number;
}

export interface CampaignLeadsResponse {
  items: CampaignLead[];
  count: number;
}

export const partnersCampaignsApi = {
  list: (params: PartnerCampaignsListParams) =>
    apiInstance.get<PartnerCampaignsListResponse>('partners/campaigns', { params }),
  read: (id: string) => apiInstance.get<PartnerCampaign>(`partners/campaigns/${id}`),
  report: (id: string, params: CampaignReportParams) =>
    apiInstance.get<CampaignReport>(`partners/campaigns/${id}/report`, { params }),
  leads: (id: string, params: CampaignLeadsParams) =>
    apiInstance.get<CampaignLeadsResponse>(`partners/campaigns/${id}/leads`, { params }),
  leadsCsv: (id: string) =>
    apiInstance.get<Blob>(`partners/campaigns/${id}/leads.csv`, { responseType: 'blob' }),
};

export const partnersCampaignsApiKeys = {
  all: ['campaigns'] as const,
  list: (params: PartnerCampaignsListParams) => ['campaigns', 'list', params] as const,
  report: (id: string, params: CampaignReportParams) =>
    ['campaigns', 'report', id, params] as const,
  leads: (id: string, params: CampaignLeadsParams) => ['campaigns', 'leads', id, params] as const,
};
